import { inject, RESPONSE_INIT } from '@angular/core';
import {
  ActivatedRouteSnapshot, RedirectCommand, ResolveFn, Router, RouterStateSnapshot, Routes
} from '@angular/router';
import { map } from 'rxjs';

import { StorefrontPage } from './model/storefront-page';
import { AdminService } from './service/admin.service';
import { ApiService } from './service/api.service';
import { SeoService } from './service/seo.service';
import { PAGE_UNAVAILABLE, PageFailure } from './service/page-failure';
import { WebsiteService } from './service/website.service';
import { AdminHierarchyPageComponent } from './component/admin/admin-hierarchy-page.component';
import { AdminOrderPageComponent } from './component/admin/admin-order-page.component';
import { AdminOrdersPageComponent } from './component/admin/admin-orders-page.component';
import { AboutPageComponent } from './component/page/about-page.component';
import { CartPageComponent } from './component/page/cart-page.component';
import { CheckoutPageComponent } from './component/page/checkout-page.component';
import { ContactsPageComponent } from './component/page/contacts-page.component';
import { IndexPageComponent } from './component/page/index-page.component';
import { PlaceOrderPageComponent } from './component/page/place-order-page.component';
import { ProductPageComponent } from './component/page/product-page.component';
import { ProductsPageComponent } from './component/page/products-page.component';
import { QuestionsPageComponent } from './component/page/questions-page.component';

function redirects(code: number | undefined): boolean {
  return code === undefined || (code >= 300 && code <= 399);
}

/**
 * Turns an API path into a route resolver. The API answers a page with either its data or the
 * redirect the Mustache controller used to return, and the redirect is followed here so no page
 * component ever renders a body it should not have.
 */
function page<T extends StorefrontPage>(
  path: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => string
): ResolveFn<T> {

  return (route, state) => {
    const router = inject(Router);
    const responseInit = inject(RESPONSE_INIT, { optional: true });
    const websiteService = inject(WebsiteService);
    const adminService = inject(AdminService);
    const seo = inject(SeoService);
    return inject(ApiService).get<T>(path(route, state)).pipe(
      map(response => {
        if (response.redirect) {
          if (response.redirectStatus && responseInit) {
            responseInit.status = response.redirectStatus;
          }
          if (redirects(response.redirectStatus)) {
            return new RedirectCommand(router.parseUrl(response.redirect), { replaceUrl: true });
          }
        }
        if (response.website) {
          websiteService.website.set(response.website);
          adminService.isAdmin.set(response.admin ?? false);
          seo.colors();
        }
        return response;
      })
    );
  };
}

/**
 * The filter, sort and paging parameters, passed on to the API untouched.
 *
 * They are read off the state's URL rather than the route snapshot: a resolver's snapshot is the
 * one for its own segment, and only the state carries the query string of the navigation.
 */
function query(state: RouterStateSnapshot): string {
  const search = parameters(state).toString();
  return search ? '?' + search : '';
}

function parameters(state: RouterStateSnapshot): URLSearchParams {
  return new URLSearchParams(state.url.split('#')[0].split('?')[1] ?? '');
}

/**
 * The order just placed. Without a code this is a stale link or a hand-typed URL: asking the API
 * for `/orders/null` only turns it into a 404, and the cart is where the visitor belongs.
 */
function placedOrder(): ResolveFn<StorefrontPage> {
  return (route, state) => {
    const code = parameters(state).get('orderCode');
    if (!code) {
      return new RedirectCommand(inject(Router).parseUrl('/cart'), { replaceUrl: true });
    }
    return page(() => '/orders/' + encodeURIComponent(code))(route, state);
  };
}

/**
 * Filtering, sorting and paging all live in the query string, and by default the router keeps the
 * page it already resolved when only that changes. Every route asks to resolve again instead — a
 * page is a single API call, so there is nothing to gain from a narrower rule.
 */
const RELOAD = 'always';

export const routes: Routes = [
  // Only after a failure: typed in, the path is left to :categoryCode like any other
  { path: PAGE_UNAVAILABLE.slice(1), children: [], canMatch: [() => !!inject(PageFailure).message()] },
  {
    path: '',
    component: IndexPageComponent,
    runGuardsAndResolvers: RELOAD,
    resolve: { data: page(() => '/pages/index') }
  },
  {
    path: 'products',
    component: ProductsPageComponent,
    runGuardsAndResolvers: RELOAD,
    resolve: { data: page((route, state) => '/pages/products' + query(state)) }
  },
  {
    path: 'cart',
    component: CartPageComponent,
    resolve: { data: page(() => '/pages/cart') }
  },
  {
    path: 'checkout',
    component: CheckoutPageComponent,
    resolve: { data: page(() => '/pages/checkout') }
  },
  {
    path: 'placeorder',
    component: PlaceOrderPageComponent,
    runGuardsAndResolvers: RELOAD,
    resolve: { data: placedOrder() }
  },
  {
    path: 'about',
    component: AboutPageComponent,
    resolve: { data: page(() => '/pages/about') }
  },
  {
    path: 'contacts',
    component: ContactsPageComponent,
    resolve: { data: page(() => '/pages/contacts') }
  },
  {
    path: 'questions',
    component: QuestionsPageComponent,
    resolve: { data: page(() => '/pages/questions') }
  },
  // The admin pages fetch their own data and check the admin cookie on every call, so their
  // resolver is only there for the shop - and they must be declared before ':categoryCode'
  // would swallow them.
  {
    path: 'orders',
    component: AdminOrdersPageComponent,
    resolve: { shop: page(() => '/pages/orders') }
  },
  {
    path: 'orders/:code',
    component: AdminOrderPageComponent,
    resolve: { shop: page(() => '/pages/orders') }
  },
  {
    path: 'product-hierarchy',
    component: AdminHierarchyPageComponent,
    resolve: { shop: page(() => '/pages/product-hierarchy') }
  },
  {
    path: ':categoryCode',
    component: ProductsPageComponent,
    runGuardsAndResolvers: RELOAD,
    resolve: {
      data: page((route, state) => '/pages/products/'
        + encodeURIComponent(route.params['categoryCode']) + query(state))
    }
  },
  {
    path: ':categoryCode/:productName',
    component: ProductPageComponent,
    runGuardsAndResolvers: RELOAD,
    resolve: {
      data: page(route => '/pages/products/' + encodeURIComponent(route.params['categoryCode'])
        + '/' + encodeURIComponent(route.params['productName']))
    }
  }
];
